// Copyright (c) 2021, Picsa Advisory Services (Pty) Ltd. All rights reserved

import React, { Component, Fragment } from 'react'
import PropTypes from 'prop-types'
import DatePicker from 'react-datepicker'
import moment from 'moment'

import {
  Grid,
  Button,
  Picklist,
  CheckboxToggle
} from '../'

import { AllOrSpecificAmountInput } from './AllOrSpecificAmountInput'

import { decodeBase64, encodeBase64 } from '../../utils'

const getTargetProductNameFromId = (id, withdrawableTargetProducts) => (
  Object.entries(withdrawableTargetProducts).find(([key, valueObj]) => (
    valueObj.id === decodeBase64(id)
  ))[0]
)
const getTargetBankaccountNameFromId = (id, withdrawableTargetBankaccounts) => (
  Object.entries(withdrawableTargetBankaccounts).find(([key, valueObj]) => (
    valueObj.id === decodeBase64(id)
  ))[0]
)


export class EditForm extends Component {
  static propTypes = {
    source: PropTypes.string,
    targetType: PropTypes.string,
    targetId: PropTypes.string,
    initiateOn: PropTypes.string,
    amountRequested: PropTypes.string,
    amountType: PropTypes.string,
    userMayOverrideDate: PropTypes.bool,
    withdrawableAccounts: PropTypes.object.isRequired,  // JSON.parsed in parent component
    withdrawableTargetProducts: PropTypes.object.isRequired,  // JSON.parsed in parent component
    withdrawableTargetBankaccounts: PropTypes.object.isRequired  // JSON.parsed in parent component
  }

  state = {
    source: this.props.source,
    target: this.props.targetType === 'product'
      ? getTargetProductNameFromId(this.props.targetId, this.props.withdrawableTargetProducts)
      : this.props.targetType === 'bankaccount'
        ? getTargetBankaccountNameFromId(this.props.targetId, this.props.withdrawableTargetBankaccounts)
        : null,
    initiationDate: this.props.initiateOn
      ? moment(this.props.initiateOn)
      : null,
    amount: this.props.amountRequested || 100,
    amountType: this.props.amountType || 'percentage',
    overrideIgnoreAcceptableDates: false
  }

  handleAmountChange = (amount, amountType='fixed') => {
    this.setState({ amount, amountType })
  }

  handleSourceChange = (sourceName) => {
    this.setState({
      source: sourceName,
      amount: 100,
      amountType: 'percentage'
    })
  }

  handleTargetChange = (targetName) => {
    this.setState({ target: targetName })
  }

  getSourceId = (name) => {
    let uuid = this.props.withdrawableAccounts[name].id

    return encodeBase64(uuid, 'AccountNode')
  }

  getTargetTypeAndId = (name) => {
    if (this.props.withdrawableTargetProducts.hasOwnProperty(name)) {
      let uuid = this.props.withdrawableTargetProducts[name].id
      return ['product', encodeBase64(uuid, 'ProductNode')]
    }
    else {  // assumption is made that if option selected is not a product, it will be a bank account
      let uuid = this.props.withdrawableTargetBankaccounts[name].id
      return ['bankaccount', encodeBase64(uuid, 'IdentityBankAccountNode')]
    }
  }

  getTargetProductMetaData = (name) => {
    if (this.props.withdrawableTargetProducts.hasOwnProperty(name)) {
      return this.props.withdrawableTargetProducts[name]
    } else {  // assumption is made that if option selected is not a product, it will be a bank account
      return null
    }
  }

  handleDateChange = (date) => {
    this.setState({ initiationDate: date })
  }

  toggleDateOverride = () => (
    this.setState(prevState => (
      { overrideIgnoreAcceptableDates: !prevState.overrideIgnoreAcceptableDates }
    ))
  )

  getSourceBalance = () => {
    if (this.state.source) {
      return this.props.withdrawableAccounts[this.state.source].current_balance
    }

    return null
  }

  getAcceptableDatesAsArrayOfNumbers = () => {
    if (this.state.source) {
      let acceptableDates = this.props.withdrawableAccounts[this.state.source].acceptable_dates
      // otherwise get acceptable_dates as comma-separated string, converting to array
      let arrayOfStrings = acceptableDates.replace(' ','').split(',')
      // next convert the array of strings, representing days, to an array of numbers
      let arrayOfNumbers = arrayOfStrings.map(dayStr => Number(dayStr))

      return arrayOfNumbers
    }

    return []
  }

  isAcceptableDate = (date) => {
    let acceptableDates = this.props.withdrawableAccounts[this.state.source].acceptable_dates
    if (acceptableDates === 'all' || this.state.overrideIgnoreAcceptableDates) {
      // handle special case where all dates are allowed OR override toggle was activated
      return true
    }
    // get day
    const day = date.date()
    acceptableDates = this.getAcceptableDatesAsArrayOfNumbers()

    return acceptableDates.indexOf(day) > -1
  }

  save = () => {
    let sourceId = this.getSourceId(this.state.source)
    let targetTypeAndId = this.getTargetTypeAndId(this.state.target)
    let targetType = targetTypeAndId[0]
    let targetId = targetTypeAndId[1]
    let initiateOn = this.state.initiationDate.format('YYYY-MM-DD')
    let targetProductMetaDataObj = this.getTargetProductMetaData(this.state.target)

    this.props.onSaveClick(sourceId, targetType, targetId, initiateOn, this.state.amount, this.state.amountType, targetProductMetaDataObj)
  }

  getTargetOptions = (withdrawableTargetProducts, withdrawableTargetBankaccounts) => {
    let allAvailableOptions = [
      ...Object.keys(withdrawableTargetProducts),
      ...Object.keys(withdrawableTargetBankaccounts)
    ]

    let optionsMinusCurrentSelectedSource = allAvailableOptions.filter(option => {
      let indexStartOfBalancePart = option.indexOf(" (balance")
      let balancePart = option.substring(indexStartOfBalancePart, option.length)
      let targetOptionWithoutBalancePart = option.replace(balancePart, '')

      return targetOptionWithoutBalancePart !== this.state.source
    })

    return optionsMinusCurrentSelectedSource
  }

  render() {
    const { withdrawableAccounts, withdrawableTargetProducts, withdrawableTargetBankaccounts, userMayOverrideDate, onCancelClick } = this.props
    const { source, target, initiationDate, amount, amountType, overrideIgnoreAcceptableDates } = this.state

    const sourceOptions = Object.keys(withdrawableAccounts)
    const targetOptions = this.getTargetOptions(withdrawableTargetProducts, withdrawableTargetBankaccounts)

    const today = moment.utc()

    return (
      <Fragment>
        <Grid
          items={[
            <div className='slds-size_1-of-3'>Withdraw from:</div>,
            <Picklist
              id='source'
              options={sourceOptions}
              value={source}
              onChange={this.handleSourceChange}
              closeDropdownOnSelect={true}
              className='slds-size_2-of-3 slds-m-bottom_medium'
            />,
            <div className='slds-size_1-of-3'>Towards:</div>,
            <Picklist
              id='target'
              options={targetOptions}
              value={targetOptions.indexOf(target) > -1
                ? target
                : null
              }
              onChange={this.handleTargetChange}
              closeDropdownOnSelect={true}
              className='slds-size_2-of-3 slds-m-bottom_medium'
            />,
            <div className='slds-size_1-of-3'>Initiation date:</div>,
            <div className='slds-size_2-of-3 slds-grid'>
              <DatePicker
                id='initiationDate'
                ref={this.dateInputRef}
                selected={initiationDate}
                onChange={this.handleDateChange}
                placeholderText='Choose date'
                dateFormat='DD/MM/YYYY'
                minDate={overrideIgnoreAcceptableDates
                  ? undefined
                  : today
                }
                filterDate={this.isAcceptableDate}
                peekNextMonth={true}
                showMonthDropdown={true}
                dropdownMode={true}
                disabled={!source || !target}
                className='slds-input slds-m-bottom_medium'
              />
              <CheckboxToggle
                id='overrideIgnoreAcceptableDates'
                label='Override?'
                title='Override default date restrictions'
                checked={overrideIgnoreAcceptableDates}
                onChange={this.toggleDateOverride}
                disabled={!userMayOverrideDate || !source || !target}
                className='slds-col_bump-left slds-p-top_xx-small'
              />
            </div>,
            <div className='slds-size_1-of-3'>Amount:</div>,
            <AllOrSpecificAmountInput
              id='amount'
              value={amount}
              allSelected={amountType
                ? amountType.toUpperCase() === 'PERCENTAGE'
                : false
              }
              onChange={this.handleAmountChange}
              sourceAccountBalance={this.getSourceBalance()}
              disabled={!source || !target || !initiationDate}
              className='slds-size_2-of-3 slds-m-bottom_medium'
            />
          ]}
          inheritItemAsIs={true}
        />
        <div className='slds-text-align_center slds-p-top_large'>
          <Button minWidth={150} onClick={onCancelClick}>
            Cancel
          </Button>
          <Button
            type='brand'
            minWidth={150}
            disabled={
              !source ||
              !target ||
              !initiationDate ||
              (amountType.toUpperCase() === "FIXED" && !amount)}
            onClick={this.save}>
            Save changes
          </Button>
        </div>
      </Fragment>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/withdrawal-add-edit-modal/EditForm.js